import * as Sentry from '@sentry/vue';
import type { Annotation, Viewer } from '@xeokit/xeokit-sdk';
import { toast } from 'vue-sonner';
import { AnnotationDto } from '~/plugins/xeokit.client';
import { getCanvasPosFromEvent } from '~/utils/viewer/get-canvas-pos-from-event';

export type AnnotationStoreState = {
  annotation: AnnotationDto | undefined;
  annotationsLoaded: boolean;
};

export const useViewerAnnotationsStore = defineStore('viewer-annotations-store', () => {
  const { $AnnotationsPlugin, $ContextMenu, $PointerCircle, $touchPointSelector } = useNuxtApp();

  const viewerStore = useViewerStore();
  const projectStore = useProjectStore();
  const toolbarStore = useToolbarStore();
  const uiStore = useUiStore();
  const { t } = useI18n();

  const isInitialized = ref(false);

  const defaultMarkerBGColor = ref('#F824FF');
  const defaultEditedMarkerBGColor = ref('#CCCCCC');
  const annotationIndex = ref(1);
  const annotationsVisible: Ref<boolean> = ref(true);

  const state: AnnotationStoreState = reactive({
    annotation: undefined,
    annotationsLoaded: false,
  });

  let plugin: InstanceType<typeof $AnnotationsPlugin> | undefined;
  let contextMenu: InstanceType<typeof $ContextMenu> | undefined;
  let setupTouchSelector: any;
  let canvasEndX: number;
  let canvasEndY: number;

  let editedAnnotation: Annotation | null = null;

  function getPlugin() {
    return plugin;
  }

  function getContextMenu() {
    return contextMenu;
  }

  function init() {
    const viewer = viewerStore.getHelpers().viewer as Viewer;

    if (!viewer) return;

    plugin = new $AnnotationsPlugin(viewer, {
      markerHTML: `<div class='annotation-marker' style='background-color: {{markerBGColor}};'>{{glyph}}</div>`,
      labelHTML: `<div class='annotation-label' style='background-color: {{labelBGColor}};'>
          <div class='annotation-title'>{{title}}</div>
          <div class='annotation-desc'>{{description}}</div>
          </div>`,
      values: {
        markerBGColor: defaultMarkerBGColor.value,
        editedMarkerBGColor: defaultEditedMarkerBGColor.value,
        labelBGColor: 'white',
        glyph: 'X',
        title: 'Untitled',
        description: 'No description',
      },
    });

    contextMenu = new $ContextMenu({
      items: [
        [
          {
            getTitle: () => t('components.xeokit_viewer.annotations.move'),
            doAction: (context) => {
              const annotation: Annotation = context.annotation;

              activateAnnotationPositionChange(annotation);
            },
          },
          {
            getTitle: () => t('components.xeokit_viewer.annotations.edit'),
            doAction: (context) => {
              const annotation: Annotation = context.annotation;
              const editedAnnotation: AnnotationDto = {
                id: annotation.id,
                glyph: annotation.getField('glyph'),
                title: annotation.getField('title'),
                description: annotation.getField('description'),
                color: annotation.getField('markerBGColor'),
                worldPos: annotation.worldPos,
              };

              state.annotation = editedAnnotation;

              uiStore.annotationDialogVisible = true;
            },
          },
        ],
        [
          {
            getTitle: () => t('components.xeokit_viewer.annotations.delete'),
            doAction: (context) => {
              deleteAnnotation(context.annotation);
            },
          },
        ],
      ],
    });

    setupTouchSelector = $touchPointSelector(viewer, new $PointerCircle(viewer), (orig, dir) => {
      const pickResult = viewer.scene.pick({
        origin: orig,
        direction: dir,
        pickSurface: true,
      });
      return pickResult && pickResult.worldPos && pickResult.worldNormal;
    });

    let prevAnnotationClicked: Annotation | null = null;

    plugin.on('markerClicked', (annotation) => {
      const labelShown = annotation.getLabelShown();

      if (prevAnnotationClicked) {
        prevAnnotationClicked.setLabelShown(false);
      }

      annotation.setLabelShown(!labelShown);

      //viewer.cameraFlight.flyTo(annotation);
      prevAnnotationClicked = annotation;
    });

    // @ts-ignore xeokit
    plugin.on('contextmenu', (annotation) => {
      const contextMenu = getContextMenu();

      if (!contextMenu) return;

      if (!toolbarStore.annotationsMode.enabled) {
        return;
      }

      contextMenu.context = {
        viewer: viewer,
        annotation: annotation,
      };

      const menuPositionX = annotation.canvasPos[0] + 20;
      const menuPositionY = annotation.canvasPos[1] + 20;

      contextMenu.show(menuPositionX, menuPositionY);
    });

    const canvasWrapperElement = document.getElementById('xeokit-wrapper') as HTMLElement;

    if (canvasWrapperElement) {
      canvasWrapperElement.addEventListener('touchstart', (e: TouchEvent) => {
        const touch = e.touches[0];
        canvasEndX = touch.clientX;
        canvasEndY = touch.clientY;
      });

      canvasWrapperElement.addEventListener('touchend', (e: TouchEvent) => {
        const touch = e.changedTouches[0];

        if (Math.abs(touch.clientX - canvasEndX) < 5 && Math.abs(touch.clientY - canvasEndY) < 5) {
          onCanvasEvent(e);
        }
      });

      canvasWrapperElement.addEventListener('mousedown', (e: MouseEvent) => {
        canvasEndX = e.offsetX;
        canvasEndY = e.offsetY;
      });

      canvasWrapperElement.addEventListener('mouseup', (e: MouseEvent) => {
        if (Math.abs(e.offsetX - canvasEndX) < 5 && Math.abs(e.offsetY - canvasEndY) < 5) {
          onCanvasEvent(e);
        }
      });
    }

    isInitialized.value = true;
  }

  async function onCanvasEvent(e: MouseEvent | TouchEvent) {
    const viewer = viewerStore.getHelpers().viewer as Viewer;
    const plugin = getPlugin();

    if (!viewer || !plugin) return;

    if (!toolbarStore.annotationsMode.actionActive && !toolbarStore.annotationsMode.active) {
      return;
    }

    const coords = getCanvasPosFromEvent(e);

    console.log({ coords });

    const pickResult = viewer.scene.pick({
      canvasPos: coords,
      pickSurface: true,
      snapToVertex: true,
    });

    console.log({
      pickResult,
      worldPos: pickResult?.worldPos,
      worldNormal: pickResult?.worldNormal,
    });

    if (pickResult && pickResult.worldPos && pickResult.worldNormal) {
      if (editedAnnotation) {
        // @ts-ignore xeokit
        editedAnnotation.setFromPickResult(pickResult);

        const editedMarkerBGColor = editedAnnotation?.getField('editedMarkerBGColor');

        editedAnnotation.setField('markerBGColor', editedMarkerBGColor);

        const editedAnnotationData: AnnotationDto = {
          id: editedAnnotation.id,
          glyph: editedAnnotation.getField('glyph'),
          title: editedAnnotation.getField('title'),
          description: editedAnnotation.getField('description'),
          color: editedAnnotation.getField('markerBGColor'),
          worldPos: editedAnnotation.worldPos,
        };

        updateAnnotation(editedAnnotationData);

        editedAnnotation = null;

        return;
      }

      if (toolbarStore.annotationsMode.actionActive) {
        const annotationId = `annotation-${annotationIndex.value}`;

        const createdAnnotation = plugin.createAnnotation({
          id: annotationId,
          pickResult: pickResult,
          labelShown: false,
          values: {
            glyph: `A${annotationIndex.value}`,
            title: `${t('components.xeokit_viewer.annotations.my_annotation')} ${annotationIndex.value}`,
            description: `${t('components.xeokit_viewer.annotations.my_description')} ${annotationIndex.value}`,
            markerBGColor: defaultMarkerBGColor.value,
            editedMarkerBGColor: defaultEditedMarkerBGColor.value,
          },
        });

        annotationIndex.value++;

        const createdAnnotationData: AnnotationDto = {
          id: createdAnnotation.id,
          glyph: createdAnnotation.getField('glyph'),
          title: createdAnnotation.getField('title'),
          description: createdAnnotation.getField('description'),
          color: createdAnnotation.getField('markerBGColor'),
          worldPos: [
            createdAnnotation.worldPos[0],
            createdAnnotation.worldPos[1],
            createdAnnotation.worldPos[2],
          ],
        };

        await saveAnnotation(createdAnnotationData, annotationId);
      }
    }
  }

  async function saveAnnotation(annotation: AnnotationDto, annotationId: string) {
    const client = useHttpClientStore();
    const project = useProjectStore();
    const projectId = project.project?.id;
    const plugin = getPlugin();

    if (!plugin || !projectId) return;

    try {
      const createdAnnotation = await client.annotations.createOne({
        projectEntryId: projectId,
        glyph: annotation.glyph,
        title: annotation.title,
        description: annotation.description,
        markerColor: annotation.color,
        positionX: annotation.worldPos[0],
        positionY: annotation.worldPos[1],
        positionZ: annotation.worldPos[2],
      });

      const createdAnnotationId = createdAnnotation.data.id;
      changeLastCreatedAnnoationId(annotationId, createdAnnotationId);
      const sceneAnnotation = plugin.annotations[createdAnnotationId];

      state.annotation = {
        id: sceneAnnotation.id,
        glyph: sceneAnnotation.getField('glyph'),
        title: sceneAnnotation.getField('title'),
        description: sceneAnnotation.getField('description'),
        color: sceneAnnotation.getField('markerBGColor'),
        worldPos: sceneAnnotation.worldPos,
      };

      uiStore.annotationDialogVisible = true;
    } catch (error: any) {
      console.log('Error saving annotation1', { error });
      toast.error(t('components.xeokit_viewer.annotations.annotation'), {
        description: t('components.xeokit_viewer.annotations.error_saving_annotation'),
        duration: 5000,
      });
      Sentry.captureException(error, {
        fingerprint: ['useViewerAnnotationsStore', 'saveAnnotation', annotationId],
      });
    }
  }

  async function updateAnnotation(annotation: AnnotationDto) {
    const plugin = getPlugin();
    if (!plugin) return;

    const annotationToUpdate = plugin.annotations[annotation.id];

    const client = useHttpClientStore();
    const project = useProjectStore();
    const projectId = project.project?.id;

    if (projectId) {
      try {
        await client.annotations.updateOne(annotation.id, {
          glyph: annotation.glyph,
          title: annotation.title,
          description: annotation.description,
          markerColor: annotation.color,
          positionX: annotation.worldPos[0],
          positionY: annotation.worldPos[1],
          positionZ: annotation.worldPos[2],
        });

        annotationToUpdate.setValues({
          glyph: annotation.glyph,
          title: annotation.title,
          description: annotation.description,
          markerBGColor: annotation.color,
        });

        toast.success(t('components.xeokit_viewer.annotations.annotation'), {
          description: t('components.xeokit_viewer.annotations.successfully_updated_annotation'),
          duration: 5000,
        });
      } catch (error: any) {
        console.log('Error saving annotation3', { error });

        toast.error(t('components.xeokit_viewer.annotations.annotation'), {
          description: t('components.xeokit_viewer.annotations.error_saving_annotation'),
          duration: 5000,
        });

        Sentry.captureException(error, {
          fingerprint: ['useViewerAnnotationsStore', 'updateAnnotation', annotation.id],
        });
      }
    }
  }

  function activateAnnotationPositionChange(annotation: Annotation) {
    editedAnnotation = annotation;
    const markerBGColor = editedAnnotation?.getField('markerBGColor');
    const editedMarkerBGColor = editedAnnotation?.getField('editedMarkerBGColor');

    editedAnnotation?.setField('editedMarkerBGColor', markerBGColor);
    editedAnnotation.setField('markerBGColor', editedMarkerBGColor);
  }

  async function deleteAnnotation(annotation: Annotation) {
    const client = useHttpClientStore();
    const plugin = getPlugin();

    if (!plugin) return;

    try {
      await client.annotations.deleteOne(annotation.id);
      plugin.destroyAnnotation(annotation.id);

      toast.success(t('components.xeokit_viewer.annotations.annotation'), {
        description: t('components.xeokit_viewer.annotations.successfully_deleted_annotation'),
        duration: 5000,
      });
    } catch (error: any) {
      toast.error(t('components.xeokit_viewer.annotations.annotation'), {
        description: t('components.xeokit_viewer.annotations.error_deleting_annotation'),
        duration: 5000,
      });

      Sentry.captureException(error, {
        fingerprint: ['useViewerAnnotationsStore', 'deleteAnnotation', annotation.id],
      });
      console.log('Error deleting annotation', { error });
    }
  }

  function changeLastCreatedAnnoationId(lastId: string, id: string) {
    const plugin = getPlugin();
    if (!plugin) return;

    const last = plugin.annotations[lastId];
    last.id = id;
    plugin.annotations[id] = last;

    delete plugin.annotations[lastId];
  }

  function toggleAnnotations() {
    const plugin = getPlugin();
    if (!plugin) return;

    annotationsVisible.value = !annotationsVisible.value;

    for (const annotation of Object.values(plugin.annotations)) {
      annotation.setMarkerShown(annotationsVisible.value);
    }
  }

  function loadAnnotations(annotationEntries: any[]) {
    if (state.annotationsLoaded) return;
    const viewerStore = useViewerStore();
    const viewer = viewerStore.getHelpers().viewer;

    if (!viewer) return;

    try {
      for (const annotation of annotationEntries) {
        const loadedAnnotation: any = {
          id: annotation.id,
          worldPos: [annotation.positionX, annotation.positionY, annotation.positionZ],
          labelShown: false,
          values: {
            glyph: annotation.glyph,
            title: annotation.title,
            description: annotation.description,
            markerBGColor: annotation.markerColor,
            editedMarkerBGColor: defaultEditedMarkerBGColor.value,
          },
        };

        if (annotation.sceneEntityId) {
          loadedAnnotation.entityId = annotation.sceneEntityId;
        }

        addAnnotation(viewer, loadedAnnotation);
      }
      state.annotationsLoaded = true;
    } catch (error: any) {
      console.log({ loadAnnotationsError: error });
      Sentry.captureException(error, {
        fingerprint: ['useViewerAnnotationsStore', 'loadAnnotations'],
      });
    }
  }

  function addAnnotation(viewer, annotation) {
    const plugin = getPlugin();
    if (!plugin) return;

    const annotationParams: any = {
      id: annotation.id,
      worldPos: annotation.worldPos,
      occludable: projectStore.hasPointCloudModel ? false : true,
      markerShown: true,
      labelShown: false,
      values: annotation.values,
    };

    if (annotation.entityId) {
      annotationParams.entity = viewer.scene.objects[annotation.entityId];
    }

    if (annotation.labelHTML) {
      annotationParams.labelHTML = annotation.labelHTML;
    }

    plugin.createAnnotation(annotationParams);
  }

  function $reset() {
    const plugin = getPlugin();
    plugin?.destroy();
    const contextMenu = getContextMenu();
    contextMenu?.destroy();
  }

  return {
    ...toRefs(state),
    isInitialized,
    getPlugin,
    init,
    toggleAnnotations,
    loadAnnotations,
    updateAnnotation,
    $reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useViewerAnnotationsStore, import.meta.hot));
}
